import { SerpAiDialog } from '../components/SerpAiDialog';
import { SerpGroupedMap } from '../components/SerpGroupedMap';
import { volumeCoverageLabel } from '../../../api/src/fakes/query-volume';
import { QueryVolumeLabel } from '../components/QueryVolumeLabel';
import { FakesVolumesDialog } from './FakesVolumesDialog';
import { SerpMapViewSelect } from '../components/SerpMapViewSelect';
import {
  DEFAULT_SERP_MAP_STYLE,
  type SerpMapView,
  type SerpMapStyle,
} from '../../../api/src/fakes/serp-appearance';
import { SerpMapStyleSelect } from '../components/SerpMapStyleSelect';
import { SerpTileMetricsMenu } from '../components/SerpTileMetricsMenu';
import {
  DEFAULT_SERP_TILE_METRICS,
  type SerpTileMetric,
} from '../../../api/src/fakes/serp-tile-metrics';
import { SerpHeatmap } from '../components/SerpHeatmap';
import { Icon } from '@iconify/react';
import {
  Alert,
  Button,
  ButtonBase,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  FAKES_MATRIX_TOP_OPTIONS,
  type FakesMatrixTop,
} from '../../../api/src/auth/user-preferences';
import { CURRENT_CLASSIFICATIONS } from '../../../api/src/fakes/serp-classification';
import { competitors } from '../../../api/src/fakes/serp-indicators';
import { FAKES_QUERY_LIMITS } from '../../../api/src/fakes/fakes-query-limits';
import { api } from '../api/client';
import { sectionById } from '../api/generated/section-catalog';
import type {
  SearchKeywordInput,
  FakesMatrixRow,
  FakesMatrixSettings,
  FakesResult,
  FakesRunPage,
  FakesSearchProfile,
  FakesMatrixPage as MatrixPage,
} from '../api/types';
import {
  searchProviderLabel,
  yandexCollectionLabel,
} from '../../../api/src/search/search-provider.types';
import { SearchKeywordDialog } from '../components/SearchKeywordDialog';
import { EditorOnly, useCanEdit } from '../components/EditorOnly';
import { FakeResultDrawer } from '../components/FakeResultDrawer';
import {
  serpTextColor,
  fakeDate,
  fakeError,
  fakeLabels,
  fakeShortLabels,
  fakeTone,
} from '../components/fakes-presentation';
import { Box, Stack, Typography } from '../components/mui-system';
import { PageHeader } from '../components/PageHeader';
import { useWorkspace } from '../workspace-context';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { FakesCollectionProgress } from '../components/FakesCollectionProgress';
import { FakesCollectionDialog } from './FakesCollectionDialog';
import { FakesReportsDialog } from './FakesReportsDialog';
import { SerpCompetitorsDialog } from './SerpCompetitorsDialog';

const rowLabels = {
  pending: 'В очереди',
  processing: 'Собираем выдачу',
  complete: 'Собрано',
  error: 'Ошибка сбора',
  missing: 'Ожидает сбора',
};
const profileText = (profile: FakesSearchProfile) =>
  `${searchProviderLabel(profile)} · ${profile.searchEngine === 'yandex' ? 'Яндекс' : 'Google'} · ${profile.regionName} · ${profile.device === 'desktop' ? 'Desktop' : 'Mobile'} · сбор Top-${profile.depth}`;

export function FakesMatrixPage() {
  const { activeWorkspace, user } = useWorkspace();
  return <UserMatrix key={user.id} workspaceId={activeWorkspace!.id} />;
}

function UserMatrix({ workspaceId }: { workspaceId: string }) {
  const [selectionParams] = useSearchParams();
  const [top, setTop] = useState<FakesMatrixTop | null>(null);
  const [mapView, setMapView] = useState<SerpMapView>('bricks');
  const [mapStyle, setMapStyle] = useState<SerpMapStyle>(DEFAULT_SERP_MAP_STYLE);
  const [tileMetrics, setTileMetrics] = useState<SerpTileMetric[]>(DEFAULT_SERP_TILE_METRICS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const live = useRef(true),
    locked = useRef(false);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setError('');
    void api
      .userPreferences()
      .then((p) => {
        if (!cancelled) {
          setTop(p.fakesMatrixTop);
          setMapView(p.serpMapView ?? 'bricks');
          setMapStyle(p.serpMapStyle ?? DEFAULT_SERP_MAP_STYLE);
          setTileMetrics(p.serpTileMetrics ?? DEFAULT_SERP_TILE_METRICS);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(`Не удалось загрузить настройки карты: ${fakeError(e)}`);
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);
  async function changeView(next: SerpMapView) {
    if (locked.current) return;
    locked.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = await api.saveUserPreferences({ serpMapView: next });
      if (live.current) setMapView(saved.serpMapView ?? 'bricks');
    } catch (e) {
      if (live.current) setError(`Не удалось сохранить вид карты: ${fakeError(e)}`);
    } finally {
      locked.current = false;
      if (live.current) setSaving(false);
    }
  }
  async function changeTop(next: FakesMatrixTop) {
    if (locked.current || next === top) return;
    locked.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = await api.saveUserPreferences({ fakesMatrixTop: next });
      if (live.current) setTop(saved.fakesMatrixTop);
    } catch (e) {
      if (live.current) setError(`Не удалось сохранить настройку Top: ${fakeError(e)}`);
    } finally {
      locked.current = false;
      if (live.current) setSaving(false);
    }
  }
  async function changeStyle(next: SerpMapStyle) {
    if (locked.current || next === mapStyle) return;
    locked.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = await api.saveUserPreferences({ serpMapStyle: next });
      if (live.current) setMapStyle(saved.serpMapStyle ?? DEFAULT_SERP_MAP_STYLE);
    } catch (e) {
      if (live.current) setError(`Не удалось сохранить оформление плиток: ${fakeError(e)}`);
    } finally {
      locked.current = false;
      if (live.current) setSaving(false);
    }
  }
  async function changeTileMetrics(next: SerpTileMetric[]) {
    if (locked.current) return;
    locked.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = await api.saveUserPreferences({ serpTileMetrics: next });
      if (live.current) setTileMetrics(saved.serpTileMetrics ?? DEFAULT_SERP_TILE_METRICS);
    } catch (e) {
      if (live.current) setError(`Не удалось сохранить показатели: ${fakeError(e)}`);
    } finally {
      locked.current = false;
      if (live.current) setSaving(false);
    }
  }
  const preferencesNotice = error && (
    <Alert
      severity="error"
      sx={{ mb: 2 }}
      action={
        top === null ? (
          <Button color="inherit" onClick={() => setAttempt((n) => n + 1)}>
            Повторить
          </Button>
        ) : undefined
      }
    >
      {error}
    </Alert>
  );
  return (
    <>
      {top === null && preferencesNotice}
      {top === null ? (
        !error && <LinearProgress aria-label="Загрузка настройки Top" />
      ) : (
        <MatrixWorkspace
          key={`${workspaceId}:${selectionParams.get('group') ?? workspaceId}`}
          workspaceId={workspaceId}
          preferencesNotice={preferencesNotice}
          top={top}
          mapView={mapView}
          mapStyle={mapStyle}
          tileMetrics={tileMetrics}
          onTileMetricsChange={(next) => void changeTileMetrics(next)}
          onMapStyleChange={(next) => void changeStyle(next)}
          onMapViewChange={(next) => void changeView(next)}
          savingTop={saving}
          onTopChange={(next) => void changeTop(next)}
        />
      )}
    </>
  );
}

function MatrixWorkspace({
  preferencesNotice,
  mapView,
  onMapViewChange,
  mapStyle,
  onMapStyleChange,
  tileMetrics,
  onTileMetricsChange,
  workspaceId,
  top,
  savingTop,
  onTopChange,
}: {
  preferencesNotice: ReactNode;
  workspaceId: string;
  top: FakesMatrixTop;
  mapView: SerpMapView;
  mapStyle: SerpMapStyle;
  tileMetrics: SerpTileMetric[];
  onTileMetricsChange(metrics: SerpTileMetric[]): void;
  onMapStyleChange(style: SerpMapStyle): void;
  onMapViewChange(view: SerpMapView): void;
  savingTop: boolean;
  onTopChange(top: FakesMatrixTop): void;
}) {
  const editable = useCanEdit('fakes.edit');
  const canRun = useCanEdit('fakes.run');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const runId = params.get('run') ?? '';
  const groupId = params.get('group') ?? workspaceId;
  const [groupDraft, setGroupDraft] = useState<{
    id?: string;
    name: string;
    revision: number;
  } | null>(null);
  const [settings, setSettings] = useState<FakesMatrixSettings | null>(null);
  const [matrix, setMatrix] = useState<MatrixPage | null>(null);
  const loadProgress = useCallback(
    () => api.fakesProgress(workspaceId, { groupId, runId: runId || undefined }),
    [workspaceId, groupId, runId],
  );
  const progress = useVisiblePolling(loadProgress);
  const loadVolumes = useCallback(() => api.fakesVolumes(workspaceId), [workspaceId]);
  const volumes = useVisiblePolling(loadVolumes);
  const [volumesOpen, setVolumesOpen] = useState(false);
  const matrixSelection = useRef('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);
  const volumeVersion = volumes.data?.latest
    ? JSON.stringify([volumes.data.latest, volumes.data.coverage])
    : null;
  useEffect(() => {
    if (volumeVersion) setVersion((v) => v + 1);
  }, [volumeVersion]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [queriesOpen, setQueriesOpen] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importMessage, setImportMessage] = useState('');
  const [profileDraft, setProfileDraft] = useState<{
    revision: number;
    profile: FakesSearchProfile | null;
    yandexSyncFallbackOn429: boolean;
  } | null>(null);
  const [confirmation, setConfirmation] = useState<{
    revision: number;
    profile: FakesSearchProfile;
    yandexSyncFallbackOn429: boolean;
    count: number;
    requestId: string;
  } | null>(null);
  const [selected, setSelected] = useState<{ result: FakesResult; row: FakesMatrixRow } | null>(
    null,
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<FakesRunPage | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyError, setHistoryError] = useState('');
  const [legacyOpen, setLegacyOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [collectionOpen, setCollectionOpen] = useState(false);
  const [competitorsOpen, setCompetitorsOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [changesOnly, setChangesOnly] = useState(false);
  const mounted = useRef(true),
    settingsRequest = useRef(0),
    mutation = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      settingsRequest.current++;
    };
  }, []);

  const loadSettings = useCallback(async () => {
    const request = ++settingsRequest.current;
    const next = await api.fakesSettings(workspaceId, groupId);
    if (mounted.current && request === settingsRequest.current) setSettings(next);
  }, [workspaceId, groupId]);
  const refresh = useCallback(async () => {
    await loadSettings();
    if (mounted.current) setVersion((v) => v + 1);
  }, [loadSettings]);
  useEffect(() => {
    setSettings(null);
    setMatrix(null);
    setProfileDraft(null);
    setConfirmation(null);
    setCollectionOpen(false);
    void loadSettings().catch((e) => {
      if (mounted.current) setError(fakeError(e));
    });
  }, [loadSettings]);
  useEffect(() => {
    if (!settings || mapView === 'groups' || mapFullscreen) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const selection = JSON.stringify([
      workspaceId,
      groupId,
      runId,
      page,
      pageSize,
      top,
      changesOnly,
      settings.profileId,
    ]);
    if (matrixSelection.current !== selection) setMatrix(null);
    matrixSelection.current = selection;
    void api
      .fakesMatrix(workspaceId, {
        runId: runId || undefined,
        groupId,
        page: page + 1,
        pageSize,
        top,
        changes: changesOnly ? 'changed' : undefined,
      })
      .then((next) => {
        if (!cancelled) {
          setMatrix(next);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(fakeError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    workspaceId,
    groupId,
    runId,
    page,
    pageSize,
    top,
    changesOnly,
    version,
    settings?.revision,
    settings?.profileId,
    Boolean(settings),
    mapView,
    mapFullscreen,
  ]);
  useEffect(() => {
    if (!historyOpen) return;
    let cancelled = false;
    setHistory(null);
    setHistoryError('');
    void api
      .fakesRuns(workspaceId, historyPage + 1, groupId)
      .then((next) => {
        if (!cancelled) setHistory(next);
      })
      .catch((e) => {
        if (!cancelled) setHistoryError(fakeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, groupId, historyOpen, historyPage, version]);
  function selectGroup(id: string, fresh = false) {
    setPage(0);
    setHistoryPage(0);
    setSelected(null);
    setMatrix(null);
    setImportText('');
    setImportMessage('');
    setQueriesOpen(false);
    setError('');
    const next = new URLSearchParams(params);
    next.set('group', id);
    if (fresh) next.delete('run');
    setParams(next);
  }
  function selectRun(id: string) {
    setPage(0);
    setSelected(null);
    setHistoryOpen(false);
    setError('');
    const next = new URLSearchParams(params);
    if (id) next.set('run', id);
    else next.delete('run');
    setParams(next);
  }
  async function act(action: () => Promise<void>) {
    if (mutation.current) return;
    mutation.current = true;
    settingsRequest.current++;
    setBusy(true);
    setError('');
    try {
      await action();
      if (mounted.current) await refresh();
      if (mounted.current) progress.refresh();
    } catch (e) {
      if (mounted.current) setError(fakeError(e));
    } finally {
      mutation.current = false;
      if (mounted.current) setBusy(false);
    }
  }
  const group = settings?.groups.find((g) => g.id === groupId);
  const groupQueries = settings?.queries.filter((q) => q.groupId === groupId) ?? [];
  const groupSizes = new Map<string, number>();
  for (const query of settings?.queries ?? [])
    groupSizes.set(query.groupId, (groupSizes.get(query.groupId) ?? 0) + 1);
  const groupLimitReached = (settings?.groups.length ?? 0) >= FAKES_QUERY_LIMITS.groupsPerWorkspace;
  const count = groupQueries.filter((q) => q.active).length;
  const registry = matrix?.registry ?? settings?.registry;
  const displayedProfile = matrix?.profile ?? settings?.profile;
  const maxTop = displayedProfile?.depth ?? 100;
  const visibleTop = Math.min(top, maxTop);
  const columns = Array.from({ length: visibleTop }, (_, i) => i + 1);
  async function saveProfile(input: SearchKeywordInput & { yandexSyncFallbackOn429?: boolean }) {
    if (!profileDraft) return;
    await act(async () => {
      const next = await api.updateFakesProfile(workspaceId, {
        provider: input.provider ?? 'xmlstock',
        yandexSearchMode: input.yandexSearchMode ?? 'deferred',
        yandexSyncFallbackOn429: input.yandexSyncFallbackOn429 ?? false,
        searchEngine: input.searchEngine,
        regionCode: input.regionCode,
        regionName: input.regionName,
        device: input.device,
        depth: input.depth,
        revision: profileDraft.revision,
        groupId,
      });
      if (!mounted.current) return;
      setSettings(next);
      setProfileDraft(null);
      selectRun('');
      setPage(0);
    });
  }

  const resultFilters = (
    <>
      <FormControlLabel
        label="Что изменилось"
        control={
          <Checkbox
            checked={changesOnly}
            onChange={(_e, checked) => {
              setChangesOnly(checked);
              setPage(0);
              setSelected(null);
            }}
          />
        }
      />
      <TextField
        select
        label="Показать Top"
        value={top}
        disabled={savingTop}
        helperText={
          savingTop
            ? 'Сохраняем…'
            : !mapFullscreen && mapView !== 'groups' && top > maxTop
              ? `В профиле доступно Top-${maxTop}`
              : undefined
        }
        onChange={(e) => {
          onTopChange(Number(e.target.value) as FakesMatrixTop);
          setSelected(null);
          setPage(0);
        }}
        sx={{ minWidth: mapFullscreen ? 110 : 140 }}
      >
        {FAKES_MATRIX_TOP_OPTIONS.map((value) => (
          <MenuItem key={value} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
    </>
  );
  return (
    <>
      {!mapFullscreen && preferencesNotice}
      <PageHeader
        title="Карта выдачи"
        description="Брендовая выдача — запросы, позиции и статусы ресурсов в одной таблице."
        action={
          <>
            <Button
              variant="outlined"
              startIcon={<Icon icon="solar:maximize-linear" />}
              onClick={() => setMapFullscreen(true)}
            >
              Карта на весь экран
            </Button>
            <Button variant="outlined" disabled={!settings} onClick={() => setVolumesOpen(true)}>
              Частотность
            </Button>
            <Button variant="outlined" onClick={() => setAiOpen(true)}>
              ИИ-разбор
            </Button>
            <Button variant="outlined" onClick={() => setCompetitorsOpen(true)}>
              Конкуренты
            </Button>
            <Button variant="outlined" onClick={() => setCollectionOpen(true)}>
              Расписание
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icon icon="solar:calendar-linear" />}
              disabled={!group}
              onClick={() => setReportsOpen(true)}
            >
              Отчёты
            </Button>
            <Button
              variant="outlined"
              disabled={!settings || !group}
              startIcon={<Icon icon="solar:list-check-linear" />}
              onClick={() => {
                setError('');
                setImportMessage('');
                setQueriesOpen(true);
              }}
            >
              Запросы {count}
            </Button>
            <EditorOnly permissions="fakes.run">
              <Button
                variant="contained"
                startIcon={<Icon icon="solar:refresh-linear" />}
                disabled={
                  busy ||
                  !settings?.profile ||
                  !count ||
                  !settings.providerConfigured ||
                  Boolean(progress.data ? progress.data.activeRun : settings.collectingRunId)
                }
                onClick={() => {
                  if (settings?.profile) {
                    setError('');
                    setConfirmation({
                      revision: settings.collectionRevision,
                      profile: settings.profile,
                      yandexSyncFallbackOn429: settings.yandexSyncFallbackOn429,
                      count,
                      requestId: crypto.randomUUID(),
                    });
                  }
                }}
              >
                Обновить выдачу
              </Button>
            </EditorOnly>
          </>
        }
      />
      {competitorsOpen && (
        <SerpCompetitorsDialog
          workspaceId={workspaceId}
          onClose={() => setCompetitorsOpen(false)}
          onChanged={refresh}
        />
      )}
      <Stack gap={2}>
        {settings && (
          <Card sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
              <TextField
                select
                label="Группа запросов"
                helperText={`Групп: ${settings.groups.length} из ${FAKES_QUERY_LIMITS.groupsPerWorkspace}`}
                value={group?.id ?? ''}
                disabled={busy}
                sx={{ minWidth: 240 }}
                onChange={(e) => selectGroup(e.target.value)}
              >
                {settings.groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name}
                  </MenuItem>
                ))}
              </TextField>
              <EditorOnly permissions="fakes.create">
                <Button
                  disabled={busy || groupLimitReached}
                  onClick={() => setGroupDraft({ name: '', revision: settings.revision })}
                >
                  Создать группу
                </Button>
              </EditorOnly>
              <EditorOnly permissions="fakes.edit">
                <Button
                  disabled={busy || !group}
                  onClick={() => group && setGroupDraft({ ...group, revision: settings.revision })}
                >
                  Переименовать
                </Button>
              </EditorOnly>
              <Typography color="text.secondary" fontSize={13}>
                Фраз в группе: {groupQueries.length} из {FAKES_QUERY_LIMITS.queriesPerGroup},
                включая отключённые. Таблица и отдельный отчёт относятся к выбранной группе.
              </Typography>
            </Stack>
          </Card>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
            <Button
              size="small"
              color="inherit"
              onClick={() => void refresh().catch((e) => setError(fakeError(e)))}
            >
              Обновить данные
            </Button>
          </Alert>
        )}
        {!settings && !error && <LinearProgress />}
        {settings && !settings.providerConfigured && (
          <Alert severity="warning">
            Выбранный провайдер выдачи не настроен на сервере. Сохранённая выдача доступна.
          </Alert>
        )}
        {registry?.stale && (
          <Alert severity="warning">
            {registry.error ||
              (!registry.configured
                ? 'Список доменов n8n не настроен.'
                : 'Список доменов n8n отсутствует или устарел.')}{' '}
            Ранее известные домены остаются нашими по последней успешной выгрузке.
          </Alert>
        )}
        <FakesCollectionProgress
          progress={progress.data}
          error={progress.error}
          changed={Boolean(
            matrix &&
            progress.data?.group &&
            progress.data.group.complete + progress.data.group.failed > 0 &&
            matrix.dataVersion !== progress.data.dataVersion &&
            Date.parse(progress.data.observedAt) >= Date.parse(matrix.classifiedAt),
          )}
          loading={loading}
          onShow={() => {
            setError('');
            setVersion((v) => v + 1);
          }}
          onJobs={() =>
            navigate(
              progress.data?.run
                ? `${sectionById('jobs').path}?job=${encodeURIComponent('fakes:' + progress.data.run.id)}`
                : sectionById('jobs').path,
            )
          }
          differentProfile={Boolean(
            progress.data?.activeRun &&
            displayedProfile &&
            progress.data.activeRun.profile.id !== displayedProfile.id,
          )}
        />
        <Card sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography fontWeight={700} mr={1}>
              {runId ? 'Профиль выбранного запуска' : 'Профиль группы'}
            </Typography>
            {displayedProfile ? (
              <>
                <Chip
                  size="small"
                  label={displayedProfile.searchEngine === 'yandex' ? 'Яндекс' : 'Google'}
                />
                <Chip size="small" label={searchProviderLabel(displayedProfile)} />
                <Chip size="small" label={displayedProfile.regionName} />
                <Chip
                  size="small"
                  label={displayedProfile.device === 'desktop' ? 'Desktop' : 'Mobile'}
                />
                <Chip size="small" label={`Сбор до ${displayedProfile.depth}`} />
                {(runId
                  ? matrix?.run?.yandexSyncFallbackOn429
                  : settings?.yandexSyncFallbackOn429) && (
                  <Chip size="small" color="warning" label="Синхронный резерв при HTTP 429" />
                )}
              </>
            ) : (
              <Typography color="text.secondary">
                Выберите параметры сбора запросов этой группы
              </Typography>
            )}
            <EditorOnly permissions="fakes.settings">
              <Button
                size="small"
                sx={{ ml: { sm: 'auto' } }}
                disabled={!settings || busy}
                onClick={() => {
                  if (settings) {
                    setError('');
                    setProfileDraft({
                      revision: settings.revision,
                      profile: settings.profile,
                      yandexSyncFallbackOn429: settings.yandexSyncFallbackOn429,
                    });
                  }
                }}
              >
                {settings?.profile ? 'Изменить профиль' : 'Настроить профиль'}
              </Button>
            </EditorOnly>
          </Stack>
        </Card>
        {settings && !settings.profile && settings.profiles.length > 1 && (
          <Alert severity="info">
            В старых запросах использовались разные настройки. Выберите профиль группы — сохранённая
            история останется доступной.
          </Alert>
        )}
        <Card>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
            p={2}
          >
            <Box>
              <Stack direction="row" alignItems="center" gap={1}>
                <Icon icon="solar:calendar-linear" />
                <Typography fontWeight={700}>
                  {matrix?.run
                    ? fakeDate(matrix.run.createdAt)
                    : loading
                      ? 'Загрузка выдачи…'
                      : 'Первый сбор ещё не запущен'}
                </Typography>
                {runId && <Chip size="small" label="История" />}
              </Stack>
              {matrix?.run && (
                <Typography fontSize={12} color="text.secondary" mt={0.5}>
                  Собрано {matrix.run.complete} из {matrix.run.total}
                  {matrix.run.failed ? ` · ошибок: ${matrix.run.failed}` : ''}
                  {matrix.run.pending
                    ? ` · в работе: ${matrix.run.pending}`
                    : matrix.run.completedAt
                      ? ` · завершено ${fakeDate(matrix.run.completedAt)}`
                      : ''}
                </Typography>
              )}
            </Box>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Button
                size="small"
                onClick={() => {
                  setHistoryPage(0);
                  setHistoryOpen(true);
                }}
              >
                История запусков
              </Button>
              {runId && (
                <Button size="small" onClick={() => selectRun('')}>
                  Текущий профиль
                </Button>
              )}
              {!mapFullscreen && resultFilters}
            </Stack>
          </Stack>
          {changesOnly && (
            <Typography px={2} pb={2} fontSize={12} color="text.secondary">
              Вход, перемещение и выход из выбранного Top относительно предыдущего успешного среза.
              Запросы без сравнения и с ошибкой сбора не включены.
            </Typography>
          )}
          {loading && <LinearProgress aria-label="Загрузка матрицы" />}
          <Box px={2} py={1}>
            <Stack direction="row" gap={2} flexWrap="wrap" alignItems="flex-start">
              <SerpMapViewSelect value={mapView} disabled={savingTop} onChange={onMapViewChange} />
              {mapView === 'groups' && (
                <SerpMapStyleSelect
                  value={mapStyle}
                  disabled={savingTop}
                  onChange={onMapStyleChange}
                />
              )}
            </Stack>
            {mapView !== 'groups' && matrix?.volumeCoverage && (
              <Typography fontSize={12} color="text.secondary" mt={1}>
                {volumeCoverageLabel(matrix.volumeCoverage)} · выбранный срез, все страницы.
              </Typography>
            )}
          </Box>
          <SerpGroupedMap
            active={mapView === 'groups' || mapFullscreen}
            mapStyle={mapStyle}
            tileMetrics={tileMetrics}
            metricsControl={
              <SerpTileMetricsMenu
                value={tileMetrics}
                disabled={savingTop}
                onChange={onTileMetricsChange}
              />
            }
            workspaceId={workspaceId}
            runId={runId || undefined}
            top={top}
            changesOnly={changesOnly}
            version={String(version)}
            fullscreen={mapFullscreen}
            fullscreenNotice={preferencesNotice}
            onCloseFullscreen={() => setMapFullscreen(false)}
            toolbar={
              mapFullscreen ? (
                <>
                  {resultFilters}
                  <SerpMapStyleSelect
                    compact
                    value={mapStyle}
                    disabled={savingTop}
                    onChange={onMapStyleChange}
                  />
                </>
              ) : undefined
            }
            onSelect={(result, row) => setSelected({ result, row })}
          />
          {mapView === 'bricks' && (
            <SerpHeatmap
              rows={matrix?.items ?? []}
              top={visibleTop}
              total={matrix?.total ?? 0}
              page={matrix?.page ?? 1}
              groupName={settings?.groups.find((g) => g.id === groupId)?.name ?? 'Группа'}
              disabled={loading}
              onSelect={(result, row) => setSelected({ result, row })}
            />
          )}
          {mapView === 'queries' && (
            <>
              <TableContainer sx={{ maxHeight: 620, overscrollBehavior: 'contain' }}>
                <Table
                  stickyHeader
                  size="small"
                  aria-label="Матрица брендовых запросов"
                  sx={{ minWidth: 220 + columns.length * 158, tableLayout: 'fixed' }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: 220,
                          position: 'sticky',
                          left: 0,
                          zIndex: 4,
                          backgroundColor: 'background.paper',
                        }}
                      >
                        Запросы
                      </TableCell>
                      {columns.map((rank) => (
                        <TableCell
                          key={rank}
                          sx={{ fontSize: 12, color: 'text.secondary', width: 158 }}
                        >
                          Top {rank}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {matrix?.items.map((row) => {
                      const fakes = row.results.filter(
                        (r) => r.rank <= visibleTop && r.classification === 'fake',
                      ).length;
                      return (
                        <TableRow key={row.queryId}>
                          <TableCell
                            component="th"
                            scope="row"
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              backgroundColor: 'background.paper',
                              borderRight: '1px solid',
                              borderRightColor: 'divider',
                            }}
                          >
                            <Typography
                              fontSize={13}
                              fontWeight={650}
                              sx={{ overflowWrap: 'anywhere' }}
                            >
                              {row.query}
                            </Typography>
                            <QueryVolumeLabel volume={row.volume} />
                            <Typography
                              fontSize={11}
                              mt={0.6}
                              color={row.status === 'error' ? 'error' : 'text.secondary'}
                            >
                              {row.status === 'complete'
                                ? `Наши ${row.results.filter((r) => r.classification === 'owned').length} · конкуренты ${row.results.filter((r) => competitors(r).length).length}`
                                : rowLabels[row.status]}
                            </Typography>
                            {row.status === 'complete' && (
                              <Typography
                                fontSize={11}
                                color={fakes ? 'error' : 'text.secondary'}
                                mt={0.5}
                              >
                                Фейки: {fakes} · Шум:{' '}
                                {row.results.filter((r) => r.relevance?.noise).length}
                              </Typography>
                            )}
                            {displayedProfile?.provider === 'yandex_search_api' &&
                              row.yandexSearchMode && (
                                <Typography
                                  fontSize={11}
                                  color={
                                    row.yandexFallbackReason ? 'warning.main' : 'text.secondary'
                                  }
                                  mt={0.5}
                                >
                                  {yandexCollectionLabel(
                                    row.yandexSearchMode,
                                    row.yandexFallbackReason,
                                  )}
                                </Typography>
                              )}
                            {row.changes && (
                              <Typography fontSize={11} color="text.secondary" mt={0.5}>
                                Вошли {row.changes.entered} · сдвиг {row.changes.moved} · вышли{' '}
                                {row.changes.dropped.length}
                              </Typography>
                            )}
                            {!!row.changes?.dropped.length && (
                              <Box component="details" sx={{ mt: 0.5, fontSize: 11 }}>
                                <summary>Вышли из Top-{visibleTop}</summary>
                                {row.changes.dropped.map((r) => (
                                  <Typography
                                    key={r.url}
                                    fontSize={11}
                                    sx={{ overflowWrap: 'anywhere', mt: 0.5 }}
                                  >
                                    Было {r.rank} · {r.url}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          {row.status === 'complete' ? (
                            columns.map((rank) => {
                              const results = row.results.filter((r) => r.rank === rank);
                              return (
                                <TableCell key={rank} sx={{ px: 0.5, py: 0.7 }}>
                                  {results.length ? (
                                    <Stack gap={0.5}>
                                      {results.map((result) => (
                                        <ResultCell
                                          key={result.id}
                                          result={result}
                                          query={row.query}
                                          hasPrevious={Boolean(row.previousSnapshot)}
                                          onClick={() => setSelected({ result, row })}
                                        />
                                      ))}
                                    </Stack>
                                  ) : (
                                    <Tooltip title="В сохранённой выдаче нет URL на этой позиции">
                                      <Box
                                        textAlign="center"
                                        color="text.secondary"
                                        aria-label={`Top ${rank}: нет результата`}
                                      >
                                        —
                                      </Box>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              );
                            })
                          ) : (
                            <TableCell colSpan={columns.length}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                gap={1}
                                p={1}
                                color={row.status === 'error' ? 'error.main' : 'text.secondary'}
                              >
                                <Icon
                                  icon={
                                    row.status === 'error'
                                      ? 'solar:danger-triangle-linear'
                                      : 'solar:clock-circle-linear'
                                  }
                                />
                                <Typography fontSize={13}>
                                  {row.error ||
                                    (row.status === 'missing'
                                      ? 'Для этого запроса нет среза в выбранном запуске.'
                                      : rowLabels[row.status])}
                                </Typography>
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                    {!loading && matrix?.total === 0 && (
                      <TableRow>
                        <TableCell colSpan={columns.length + 1}>
                          <Box p={3}>
                            <Typography>
                              {changesOnly
                                ? 'Изменений в выбранном Top не найдено среди запросов с сопоставимыми срезами.'
                                : 'Добавьте брендовые запросы списком, выберите профиль группы и запустите сбор.'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack
                direction="row"
                gap={2}
                flexWrap="wrap"
                p={2}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Typography fontSize={11} color="text.secondary">
                  ≋ Шум — метка для запроса и URL
                </Typography>
                {CURRENT_CLASSIFICATIONS.map((kind) => (
                  <Stack key={kind} direction="row" alignItems="center" gap={0.7}>
                    <Box
                      sx={(theme: Theme) => ({
                        width: 9,
                        height: 9,
                        borderRadius: 0.7,
                        backgroundColor: fakeTone(theme, kind),
                      })}
                    />
                    <Typography fontSize={11}>{fakeLabels[kind]}</Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
          {mapView !== 'groups' && (
            <TablePagination
              component="div"
              count={matrix?.total ?? 0}
              page={page}
              rowsPerPage={pageSize}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Запросов на странице"
              onPageChange={(_e, value) => {
                setSelected(null);
                setPage(value);
              }}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(0);
              }}
            />
          )}
        </Card>
        <Stack
          direction="row"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography fontSize={12} color="text.secondary">
              n8n: {registry?.count ?? 0} доменов · {fakeDate(registry?.syncedAt)}
            </Typography>
            <Typography fontSize={11} color="text.secondary">
              Статусы по текущим реестрам, донорам и решениям. Динамика относится к конкретному URL.
            </Typography>
          </Box>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <EditorOnly permissions="fakes.run">
              <Button
                size="small"
                disabled={busy || !registry?.configured}
                onClick={() =>
                  void act(async () => {
                    const result = await api.syncFakesRegistry(workspaceId);
                    if (result.error) throw new Error(result.error);
                  })
                }
              >
                Обновить реестр
              </Button>
            </EditorOnly>
            {Boolean(settings?.legacyKeywords.length) && (
              <Button size="small" onClick={() => setLegacyOpen(true)}>
                Отдельные срезы
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Drawer
        anchor="right"
        open={queriesOpen}
        onClose={() => !busy && setQueriesOpen(false)}
        slotProps={{
          paper: {
            sx: { width: { xs: '100%', sm: 440 }, maxWidth: '100%' },
            role: 'dialog',
            'aria-label': 'Брендовые запросы',
          },
        }}
      >
        <Stack gap={2} p={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h3">Брендовые запросы</Typography>
            <IconButton
              aria-label="Закрыть запросы"
              disabled={busy}
              onClick={() => setQueriesOpen(false)}
            >
              <Icon icon="solar:close-circle-linear" />
            </IconButton>
          </Stack>
          <Typography fontSize={13} color="text.secondary">
            Группа «{group?.name}». Отключённые запросы сохраняют историю. Перенос в другую группу
            применяется к будущим сборам. Признак «Приоритетный» не зависит от группы и также
            фиксируется при следующем сборе.
          </Typography>
          <Typography fontSize={13} color="text.secondary">
            Фраз в группе: {groupQueries.length} из {FAKES_QUERY_LIMITS.queriesPerGroup}, включая
            отключённые. Свободно:{' '}
            {Math.max(0, FAKES_QUERY_LIMITS.queriesPerGroup - groupQueries.length)}.
          </Typography>
          {groupQueries.length > FAKES_QUERY_LIMITS.queriesPerGroup && (
            <Alert severity="warning">
              В группе превышен лимит. Существующие фразы сохранены; перед добавлением новых
              перенесите часть в другую группу.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
            {groupQueries.map((query) => (
              <Stack key={query.id} gap={1} mb={2}>
                <FormControlLabel
                  sx={{
                    display: 'flex',
                    m: 0,
                    '& .MuiFormControlLabel-label': { fontSize: 13, overflowWrap: 'anywhere' },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={query.active}
                      disabled={!editable || busy}
                      onChange={(_e, checked) =>
                        void act(async () => {
                          const next = await api.updateFakesQuery(workspaceId, query.id, {
                            active: checked,
                            revision: settings!.revision,
                          });
                          if (mounted.current) setSettings(next);
                          setPage(0);
                        })
                      }
                    />
                  }
                  label={query.query}
                />
                <FormControlLabel
                  label="Приоритетный"
                  control={
                    <Checkbox
                      size="small"
                      checked={query.priority}
                      disabled={!editable || busy}
                      slotProps={{ input: { 'aria-label': `Приоритетный: ${query.query}` } }}
                      onChange={(_e, priority) =>
                        void act(async () => {
                          const next = await api.updateFakesQuery(workspaceId, query.id, {
                            priority,
                            revision: settings!.revision,
                          });
                          if (mounted.current) setSettings(next);
                        })
                      }
                    />
                  }
                />
                <TextField
                  select
                  size="small"
                  label={`Группа для «${query.query}»`}
                  value={query.groupId}
                  disabled={!editable || busy}
                  onChange={(e) => {
                    const target = e.target.value;
                    void act(async () => {
                      const next = await api.updateFakesQuery(workspaceId, query.id, {
                        groupId: target,
                        revision: settings!.revision,
                      });
                      if (mounted.current) setSettings(next);
                      setPage(0);
                    });
                  }}
                >
                  {settings?.groups.map((g) => (
                    <MenuItem
                      key={g.id}
                      value={g.id}
                      disabled={
                        g.id !== query.groupId &&
                        (groupSizes.get(g.id) ?? 0) >= FAKES_QUERY_LIMITS.queriesPerGroup
                      }
                    >
                      {g.name} · {groupSizes.get(g.id) ?? 0}/{FAKES_QUERY_LIMITS.queriesPerGroup}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            ))}
          </Box>
          <EditorOnly permissions="fakes.import">
            <TextField
              label="Добавить списком — один запрос на строку"
              multiline
              minRows={5}
              value={importText}
              disabled={busy}
              onChange={(e) => setImportText(e.target.value)}
              helperText={`В группе — до ${FAKES_QUERY_LIMITS.queriesPerGroup} фраз, включая отключённые. Пустые строки и повторы будут пропущены. При превышении лимита весь импорт отклоняется. Сбор не запускается.`}
            />
          </EditorOnly>
          <EditorOnly permissions="fakes.import">
            <Button
              variant="contained"
              disabled={busy || !importText.trim() || !settings}
              onClick={() =>
                void act(async () => {
                  const lines = importText.split(/\r?\n/);
                  const response = await api.importFakesQueries(workspaceId, {
                    queries: lines,
                    groupId,
                    revision: settings!.revision,
                  });
                  if (!mounted.current) return;
                  setSettings(response.state);
                  setImportText('');
                  setImportMessage(
                    `Добавлено: ${response.added}. Пропущено пустых строк и повторов: ${response.skipped}.`,
                  );
                })
              }
            >
              Добавить запросы
            </Button>
          </EditorOnly>
          {importMessage && (
            <Alert severity="success" role="status">
              {importMessage}
            </Alert>
          )}
        </Stack>
      </Drawer>
      <Dialog
        open={Boolean(groupDraft)}
        onClose={() => !busy && setGroupDraft(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {groupDraft?.id ? 'Переименовать группу' : 'Создать группу запросов'}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              autoFocus
              label="Название группы"
              value={groupDraft?.name ?? ''}
              disabled={busy}
              slotProps={{ htmlInput: { maxLength: 100 } }}
              onChange={(e) =>
                setGroupDraft((draft) => draft && { ...draft, name: e.target.value })
              }
            />
            {!groupDraft?.id && (
              <Typography color="text.secondary">
                Выберите профиль новой группы и добавьте запросы. Расписание сбора и отчёты
                включаются отдельно, когда группа будет готова.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setGroupDraft(null)}>
            Отмена
          </Button>
          <Button
            variant="contained"
            disabled={busy || !groupDraft?.name.trim() || (!groupDraft?.id && groupLimitReached)}
            onClick={() =>
              void act(async () => {
                if (!groupDraft) return;
                const result = await api.saveFakesGroup(
                  workspaceId,
                  { name: groupDraft.name, revision: groupDraft.revision },
                  groupDraft.id,
                );
                if (!mounted.current) return;
                setSettings(result.state);
                selectGroup(result.group.id, !groupDraft.id);
                setGroupDraft(null);
              })
            }
          >
            Сохранить группу
          </Button>
        </DialogActions>
      </Dialog>
      {profileDraft && (
        <SearchKeywordDialog
          key={`${profileDraft.revision}:${profileDraft.profile?.id ?? 'new'}`}
          mode="profile"
          initialConfig={profileDraft.profile ?? undefined}
          initialSyncFallbackOn429={profileDraft.yandexSyncFallbackOn429}
          providers={settings?.providers}
          error={error}
          busy={busy}
          open
          workspaceId={workspaceId}
          onClose={() => !busy && setProfileDraft(null)}
          onSubmit={saveProfile}
        />
      )}
      <Dialog
        open={Boolean(confirmation)}
        onClose={() => !busy && setConfirmation(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Обновить выдачу?</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>
              Будет выполнен сбор по {confirmation?.count} активным запросам выбранной группы.
              Выбранный провайдер расходует баланс. Прежние запуски останутся в истории.
            </Typography>
            {confirmation?.profile.provider === 'yandex_search_api' && (
              <Alert
                severity={confirmation.profile.yandexSearchMode === 'sync' ? 'warning' : 'info'}
              >
                {confirmation.profile.yandexSearchMode === 'sync'
                  ? 'Синхронный сбор Yandex Search API: результат приходит сразу, стоимость значительно выше отложенного режима.'
                  : 'Отложенный сбор Yandex Search API: результат может готовиться от 5 минут до нескольких часов. Страницу можно закрыть.'}
              </Alert>
            )}
            {confirmation?.yandexSyncFallbackOn429 && (
              <Alert severity="warning">
                При HTTP 429 отложенного запроса разрешена одна более дорогая синхронная попытка.
              </Alert>
            )}
            {confirmation && (
              <Typography fontWeight={650}>{profileText(confirmation.profile)}</Typography>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setConfirmation(null)}>
            Отмена
          </Button>
          <EditorOnly permissions="fakes.run">
            <Button
              variant="contained"
              disabled={busy}
              onClick={() =>
                void act(async () => {
                  if (!confirmation) return;
                  await api.startFakesRun(workspaceId, {
                    profileId: confirmation.profile.id,
                    requestId: confirmation.requestId,
                    revision: confirmation.revision,
                    groupId,
                  });
                  if (mounted.current) {
                    setConfirmation(null);
                    selectRun('');
                    setPage(0);
                  }
                })
              }
            >
              Запустить сбор
            </Button>
          </EditorOnly>
        </DialogActions>
      </Dialog>
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>История запусков группы</DialogTitle>
        <DialogContent>
          {historyError && <Alert severity="error">{historyError}</Alert>}
          {!history && !historyError && <LinearProgress />}
          <Stack gap={2}>
            {history?.items.map((run) => (
              <Stack
                key={run.id}
                direction="row"
                gap={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography fontWeight={650}>{fakeDate(run.createdAt)}</Typography>
                  <Typography color="text.secondary" fontSize={12}>
                    {profileText(run.profile)}
                  </Typography>
                  <Typography fontSize={12}>
                    Собрано {run.complete}/{run.total}
                    {run.failed ? ` · ошибок: ${run.failed}` : ''}
                    {run.pending ? ` · в работе: ${run.pending}` : ''}
                  </Typography>
                </Box>
                <Button onClick={() => selectRun(run.id)}>Открыть</Button>
              </Stack>
            ))}
            {history?.total === 0 && (
              <Typography color="text.secondary">
                Общих запусков ещё нет. Старые результаты доступны в «Отдельных срезах».
              </Typography>
            )}
          </Stack>
          <TablePagination
            component="div"
            count={history?.total ?? 0}
            page={historyPage}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
            onPageChange={(_e, value) => setHistoryPage(value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      {volumesOpen && settings && (
        <FakesVolumesDialog
          workspaceId={workspaceId}
          settings={settings}
          state={volumes.data}
          error={volumes.error}
          editable={canRun}
          onStarted={volumes.refresh}
          onClose={() => setVolumesOpen(false)}
        />
      )}
      {collectionOpen && (
        <FakesCollectionDialog
          workspace={workspaceId}
          groupId={groupId}
          groupName={group?.name ?? 'Группа'}
          count={count}
          profile={settings?.profile ?? null}
          yandexSyncFallbackOn429={settings?.yandexSyncFallbackOn429 ?? false}
          onClose={() => setCollectionOpen(false)}
        />
      )}
      {reportsOpen && group && (
        <FakesReportsDialog
          groups={settings!.groups.map((g) => ({
            ...g,
            count: settings!.queries.filter((q) => q.active && q.groupId === g.id).length,
          }))}
          key={groupId}
          groupId={groupId}
          groupName={group.name}
          workspace={workspaceId}
          count={count}
          profile={settings?.profile ?? null}
          onClose={() => setReportsOpen(false)}
        />
      )}
      <Dialog open={legacyOpen} onClose={() => setLegacyOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>История отдельных запросов</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography color="text.secondary" fontSize={13}>
              Эти срезы не объединяются в общий запуск задним числом.
            </Typography>
            {settings?.legacyKeywords.map((keyword) => (
              <Stack
                key={keyword.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={2}
              >
                <Box>
                  <Typography>{keyword.query}</Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {keyword.searchEngine === 'yandex' ? 'Яндекс' : 'Google'} · {keyword.regionName}{' '}
                    · {keyword.device} · Top-{keyword.depth}
                  </Typography>
                </Box>
                <Button onClick={() => navigate(`${sectionById('fakes').path}/${keyword.id}`)}>
                  Открыть
                </Button>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegacyOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      {aiOpen && (
        <SerpAiDialog
          key={[workspaceId, groupId, runId, visibleTop, changesOnly].join(':')}
          workspaceId={workspaceId}
          scope={{
            groupId,
            runId: runId || undefined,
            top: visibleTop,
            changes: changesOnly ? 'changed' : undefined,
          }}
          candidates={(matrix?.items ?? [])
            .flatMap((row) => row.results)
            .filter((row) => row.rank <= visibleTop)}
          onClose={() => setAiOpen(false)}
          onSaved={refresh}
        />
      )}
      {selected && (
        <FakeResultDrawer
          key={selected.result.id}
          workspaceId={workspaceId}
          result={selected.result}
          query={selected.row.query}
          capturedAt={selected.row.capturedAt}
          previousCapturedAt={selected.row.previousSnapshot?.capturedAt}
          onClose={() => setSelected(null)}
          onSaved={refresh}
        />
      )}
    </>
  );
}

function ResultCell({
  result,
  query,
  hasPrevious,
  onClick,
}: {
  result: FakesResult;
  query: string;
  hasPrevious: boolean;
  onClick: () => void;
}) {
  const tone = (theme: Theme) => fakeTone(theme, result.classification);
  const rivals = competitors(result);
  const movement =
    result.delta === null
      ? hasPrevious
        ? 'нов.'
        : '—'
      : result.delta > 0
        ? `↑ ${result.delta}`
        : result.delta < 0
          ? `↓ ${Math.abs(result.delta)}`
          : '—';
  return (
    <Tooltip
      title={
        <>
          <div>{result.url}</div>
          <div>{result.reason}</div>
          {result.relevance?.noise && <div>Шум: {result.relevance.reason}</div>}
        </>
      }
    >
      <ButtonBase
        onClick={onClick}
        aria-label={`${query}, позиция ${result.rank}, ${result.telegramUsername ? '@' + result.telegramUsername : result.hostname}, ${fakeLabels[result.classification]}${rivals.length ? `, конкурент: ${rivals.map((r) => r.title).join(', ')}` : ''}${result.relevance?.noise ? ', шум' : ''}`}
        sx={(theme: Theme) => ({
          width: '100%',
          minHeight: 64,
          px: 1.3,
          py: 1.1,
          display: 'block',
          textAlign: 'left',
          borderRadius: 1.5,
          backgroundColor: alpha(tone(theme), theme.palette.mode === 'dark' ? 0.14 : 0.09),
          border: '1px solid',
          borderColor: 'transparent',
          '&:hover': { borderColor: alpha(tone(theme), 0.55) },
          '&.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 1,
          },
        })}
      >
        <Typography fontSize={12} fontWeight={650} noWrap>
          {result.telegramUsername ? '@' + result.telegramUsername : result.hostname}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5} mt={1}>
          <Typography
            component="span"
            fontSize={11}
            sx={(theme: Theme) => ({
              color: serpTextColor(theme, tone(theme)),
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              whiteSpace: 'nowrap',
            })}
          >
            <Box
              component="span"
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: 'currentColor',
                flexShrink: 0,
              }}
            />
            {fakeShortLabels[result.classification]}
            {result.domainStatus === 'dead' ? ' · dead' : ''}
          </Typography>
          <Typography
            component="span"
            fontSize={11}
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          >
            {movement}
          </Typography>
        </Stack>
        {(rivals.length > 0 || result.relevance?.noise) && (
          <Typography
            fontSize={10}
            color="text.secondary"
            sx={{ mt: 0.6, overflowWrap: 'anywhere' }}
          >
            {rivals.map((r) => r.title).join(', ')}
            {result.relevance?.noise ? `${rivals.length ? ' · ' : ''}≋ Шум` : ''}
          </Typography>
        )}
      </ButtonBase>
    </Tooltip>
  );
}
